import { forwardRef, useImperativeHandle, useRef } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { Card } from '@/components/ui/card/Card';

import { DropDownList } from '@/components/form/inputs/DropDownList';
import { MultipleCheckbox } from '@/components/form/inputs/MultipleCheckBoxes';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { useTranslation } from '@/hooks/useTranslation';
import { ClipboardList, MessageSquare, Sparkles } from 'lucide-react';

import { toolChange, toolState } from '../../data/toolState';

type BreakTabProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
};

export const BreakTab = forwardRef(function BreakTab({ values, onChange }: BreakTabProps, ref) {
  const input1Ref = useRef<{ validate: () => boolean }>(null);
  const input2Ref = useRef<{ validate: () => boolean }>(null);
  const input3Ref = useRef<{ validate: () => boolean }>(null);
  const { t } = useTranslation();
  useImperativeHandle(ref, () => ({
    validate: () => {
      const v1 = input1Ref.current?.validate() ?? true;
      const v2 = input2Ref.current?.validate() ?? true;
      const v3 = input3Ref.current?.validate() ?? true;

      return v1 && v2 && v3;
    },
  }));

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {/* Left column: state + comment */}
      <div className="flex flex-col gap-5">
        {/* Tool condition */}
        <Card padding="sm">
          <div className="flex flex-col gap-2">
            <div className="space-y-1">
              <SectionLabel icon={Sparkles}>{t('traceability.breakTab.stateTitle')}</SectionLabel>
              <p className="text-sm text-muted-foreground">
                {t('traceability.breakTab.stateDescription')}
              </p>
            </div>
            <div className="mt-3">
              <DropDownList
                ref={input1Ref}
                name="toolState"
                label={t('traceability.breakTab.stateLabel')}
                value={(values['toolState'] as string) ?? ''}
                onChange={(val) => onChange('toolState', val)}
                options={toolState.map(({ value, labelKey }) => ({
                  value,
                  label: t(labelKey),
                }))}
                required
                side="bottom"
              />
            </div>
          </div>
        </Card>

        {/* Note */}
        <Card padding="sm" className="flex flex-1 flex-col">
          <div className="flex flex-col gap-2">
            <div className="space-y-1">
              <SectionLabel icon={MessageSquare}>
                {t('traceability.breakTab.commentLabel')}
              </SectionLabel>
            </div>
            <div className="mt-3">
              <SimpleInput
                ref={input3Ref}
                name="commentary"
                label={t('traceability.breakTab.commentLabel')}
                value={(values['commentary'] as string) ?? ''}
                onChange={(val) => onChange('commentary', val)}
                placeholder={t('traceability.breakTab.commentPlaceholder')}
                textarea
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Right column: reasons */}
      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <div className="space-y-1">
            <SectionLabel icon={ClipboardList}>
              {t('traceability.breakTab.reasonTitle')}
            </SectionLabel>
            <p className="text-sm text-muted-foreground">
              {t('traceability.breakTab.reasonDescription')}
            </p>
          </div>
          <div className="mt-3">
            <MultipleCheckbox
              ref={input2Ref}
              name="toolChange"
              label={t('traceability.breakTab.reasonLabel')}
              value={(values['toolChange'] as string[]) ?? []}
              onChange={(val) => onChange('toolChange', val)}
              options={toolChange.map(({ value, labelKey }) => ({
                value,
                label: t(labelKey),
              }))}
              required
            />
          </div>
        </div>
      </Card>
    </div>
  );
});
